import { useEffect, useMemo, useState } from 'react';
import type { ImageFilter, ImageMask } from '@ox/schema';
import { removeImageBackground } from '../lib/removeBackground';
import { fetchAssetMeta, uploadImageFile } from '../lib/uploadAsset';
import { EDITOR_MASKS, maskImageUrl } from '../lib/masks';
import { EDITOR_FILTERS, resolveImageFilter } from '../lib/imageFilters';
import { MaskFitPreview, preloadImageUrl, type MaskFitState } from './MaskFitPreview';
import { FilterThumb } from './FilterThumb';

export type PendingImageDraft = {
  assetId: string;
  previewUrl: string;
  widthMm: number;
  heightMm: number;
  mask?: ImageMask;
  filter?: ImageFilter;
  /** @deprecated prefer filter */
  grayscale?: boolean;
  maskScale?: number;
  maskOffsetX?: number;
  maskOffsetY?: number;
};

export type EditImageResult = {
  assetId: string;
  widthMm: number;
  heightMm: number;
  mask: ImageMask;
  filter: ImageFilter;
  maskScale: number;
  maskOffsetX: number;
  maskOffsetY: number;
};

const DEFAULT_FIT: MaskFitState = { maskScale: 1, maskOffsetX: 0, maskOffsetY: 0 };

export function EditImageModal({
  apiBase,
  draft,
  getToken,
  onClose,
  onUse,
  uploading = false,
}: {
  apiBase: string;
  draft: PendingImageDraft;
  getToken: () => Promise<string | null | undefined>;
  onClose: () => void;
  onUse: (result: EditImageResult) => void;
  /** True while the file is still uploading / assetId not ready. */
  uploading?: boolean;
}) {
  const [tab, setTab] = useState<'shapes' | 'bg' | 'effects'>('shapes');
  const [mask, setMask] = useState<ImageMask>(draft.mask ?? 'none');
  const [fit, setFit] = useState<MaskFitState>({
    maskScale: draft.maskScale ?? 1,
    maskOffsetX: draft.maskOffsetX ?? 0,
    maskOffsetY: draft.maskOffsetY ?? 0,
  });
  const [filter, setFilter] = useState<ImageFilter>(
    resolveImageFilter(draft.filter, draft.grayscale),
  );
  const [removeBg, setRemoveBg] = useState(false);
  const [removeInterior, setRemoveInterior] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState(draft.previewUrl);
  const [previewUrl, setPreviewUrl] = useState(draft.previewUrl);
  const [processedAssetId, setProcessedAssetId] = useState<string | null>(null);
  const [using, setUsing] = useState(false);

  const assetReady = !!(processedAssetId || draft.assetId?.trim());
  const stillUploading = uploading || !assetReady;
  const canUse = !using && !busy && !stillUploading && assetReady;

  useEffect(() => {
    // Upload finished — unlock Usar imagen if a premature click left `using` stuck.
    if (draft.assetId?.trim()) setUsing(false);
  }, [draft.assetId]);

  useEffect(() => {
    setBaseUrl(draft.previewUrl);
    setPreviewUrl(draft.previewUrl);
    setProcessedAssetId(null);
    setRemoveBg(false);
    setMask(draft.mask ?? 'none');
    setFit({
      maskScale: draft.maskScale ?? 1,
      maskOffsetX: draft.maskOffsetX ?? 0,
      maskOffsetY: draft.maskOffsetY ?? 0,
    });
    setFilter(resolveImageFilter(draft.filter, draft.grayscale));
    setError(null);
    void (async () => {
      if (!draft.assetId?.trim()) return;
      const token = await getToken();
      const meta = await fetchAssetMeta(apiBase, draft.assetId, token ?? null);
      const url = meta?.proxyUrl || meta?.thumbUrl || meta?.originalUrl;
      if (!url) return;
      if (await preloadImageUrl(url)) {
        setBaseUrl(url);
        setPreviewUrl(url);
      }
    })();
  }, [
    draft.assetId,
    draft.previewUrl,
    apiBase,
    getToken,
    draft.mask,
    draft.filter,
    draft.grayscale,
    draft.maskScale,
    draft.maskOffsetX,
    draft.maskOffsetY,
  ]);

  const previewClass = useMemo(() => {
    const parts = ['ox-edit-img-preview-frame'];
    if (removeBg) parts.push('ox-edit-checker');
    return parts.join(' ');
  }, [removeBg]);

  async function runRemoveBg() {
    setBusy(true);
    setError(null);
    try {
      const blob = await removeImageBackground(baseUrl, { interior: removeInterior });
      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      const token = await getToken();
      const uploaded = await uploadImageFile(apiBase, blob, {
        filename: 'no-bg.png',
        contentType: 'image/png',
        token: token ?? null,
      });
      setProcessedAssetId(uploaded.assetId);
      const metaUrl = uploaded.proxyUrl || uploaded.thumbUrl || uploaded.originalUrl || objectUrl;
      setPreviewUrl(metaUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar el fondo');
      setRemoveBg(false);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!removeBg) {
      setPreviewUrl(baseUrl);
      setProcessedAssetId(null);
      return;
    }
    void runRemoveBg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeBg]);

  return (
    <div
      className="ox-modal-backdrop"
      role="presentation"
      onClick={stillUploading ? undefined : onClose}
    >
      <div
        className="ox-modal ox-edit-img-modal"
        role="dialog"
        aria-label="Editar imagen"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ox-edit-img-head">
          <h2>Editar imagen</h2>
          <div className="ox-edit-img-head-actions">
            <button
              type="button"
              className="ox-primary"
              disabled={!canUse}
              onClick={() => {
                const id = processedAssetId || draft.assetId;
                if (!id?.trim()) return;
                setUsing(true);
                const applied = mask === 'none' ? DEFAULT_FIT : fit;
                onUse({
                  assetId: id,
                  widthMm: draft.widthMm,
                  heightMm: draft.heightMm,
                  mask,
                  filter,
                  ...applied,
                });
              }}
            >
              {stillUploading ? 'Subiendo…' : '✓ Usar imagen'}
            </button>
            <button
              type="button"
              className="ox-edit-img-x"
              onClick={stillUploading ? undefined : onClose}
              disabled={stillUploading}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="ox-edit-img-body">
          <nav className="ox-edit-img-nav">
            <button
              type="button"
              className={tab === 'shapes' ? 'active' : ''}
              onClick={() => setTab('shapes')}
            >
              <span className="ox-edit-nav-ico" aria-hidden>
                ◇
              </span>
              Formas con foto
            </button>
            <button type="button" className={tab === 'bg' ? 'active' : ''} onClick={() => setTab('bg')}>
              <span className="ox-edit-nav-ico" aria-hidden>
                ▤
              </span>
              Quitar fondo
            </button>
            <button
              type="button"
              className={tab === 'effects' ? 'active' : ''}
              onClick={() => setTab('effects')}
            >
              <span className="ox-edit-nav-ico" aria-hidden>
                ✦
              </span>
              Efectos
            </button>
          </nav>

          <div className="ox-edit-img-tools">
            {tab === 'shapes' && (
              <>
                <p className="ox-hint">Elige un marco y encuadra la foto arrastrando o con el zoom.</p>
                <div className="ox-edit-mask-grid">
                  {EDITOR_MASKS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={mask === m.id ? 'active' : ''}
                      disabled={stillUploading}
                      onClick={() => {
                        setMask(m.id);
                        setFit(DEFAULT_FIT);
                      }}
                    >
                      <span
                        className={`ox-m-mask-ico ox-m-mask-${m.id}`}
                        style={
                          maskImageUrl(m.id)
                            ? {
                                WebkitMaskImage: `url(${maskImageUrl(m.id)})`,
                                maskImage: `url(${maskImageUrl(m.id)})`,
                              }
                            : undefined
                        }
                      />
                      {m.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {tab === 'bg' && (
              <>
                <label className="ox-toggle-row">
                  <span>Eliminar el fondo de la imagen</span>
                  <input
                    type="checkbox"
                    className="ox-toggle"
                    checked={removeBg}
                    disabled={busy || stillUploading}
                    onChange={(e) => setRemoveBg(e.target.checked)}
                  />
                </label>
                <label className="ox-toggle-row">
                  <span>Eliminar también del interior del dibujo</span>
                  <input
                    type="checkbox"
                    className="ox-toggle"
                    checked={removeInterior}
                    disabled={!removeBg || busy || stillUploading}
                    onChange={(e) => {
                      setRemoveInterior(e.target.checked);
                      if (removeBg) {
                        setRemoveBg(false);
                        queueMicrotask(() => setRemoveBg(true));
                      }
                    }}
                  />
                </label>
                {removeBg && (
                  <div className="ox-edit-bg-actions">
                    <p className="ox-hint">Revisa si el resultado es el esperado</p>
                    <button
                      type="button"
                      className="ox-secondary-btn"
                      disabled={busy}
                      onClick={() => void runRemoveBg()}
                    >
                      Volver a intentarlo
                    </button>
                  </div>
                )}
                {busy && <p className="ox-hint">Procesando…</p>}
                {error && <p className="ox-warn">{error}</p>}
              </>
            )}

            {tab === 'effects' && (
              <>
                <p className="ox-hint">Filtros de color (misma familia que Fabric.js / FPD).</p>
                <div className="ox-edit-filter-grid">
                  {EDITOR_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={filter === f.id ? 'active' : ''}
                      disabled={stillUploading}
                      onClick={() => setFilter(f.id)}
                    >
                      <FilterThumb src={previewUrl} filter={f.id} />
                      <span>{f.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className={`${previewClass}${stillUploading ? ' ox-edit-img-preview--uploading' : ''}`}>
            {previewUrl ? (
              <MaskFitPreview
                key={previewUrl}
                src={previewUrl}
                mask={mask}
                filter={filter}
                fit={fit}
                onFitChange={setFit}
              />
            ) : (
              <div className="ox-m-edit-preview-loading" aria-live="polite">
                <div className="ox-upload-spinner" aria-hidden />
                <span>Cargando imagen…</span>
              </div>
            )}
            {stillUploading && previewUrl ? (
              <div className="ox-edit-img-upload-badge" aria-live="polite">
                <span className="ox-upload-spinner ox-upload-spinner--sm" aria-hidden />
                Subiendo imagen…
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
